import {useMemo} from 'react';
import i18next from 'i18next';
import {type Moment} from 'moment-timezone';

import {type SessionsVariables} from 'web-app/api/clients/rest-types/sessions/sessions';
import {type SessionsResponse} from 'web-app/api/clients/rest-types/sessions/sessions-query-data';
import {useTransformQuery} from 'web-app/react/hooks/use-transform-query';
import {
    type GetBillingProfilesQuery,
    type GetBillingProfilesQueryVariables,
} from 'web-app/react/invoicing-profiles/overview/__generated__/queries.api-types';
import {GetBillingProfiles} from 'web-app/react/invoicing-profiles/overview/queries';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import type {ProductsData, ProductsVariables} from 'web-app/api/clients/rest-types/products/products';
import type {
    FetchAutomaticInvoicingConfigQuery,
    FetchAutomaticInvoicingConfigQueryVariables,
} from 'web-app/react/routes/account/institution/automatic-invoicing-config/__generated__/queries.api-types';
import {FetchAutomaticInvoicingConfig} from 'web-app/react/routes/account/institution/automatic-invoicing-config/queries';
import {type DiscountsQueryVariables, type DiscountsQuery} from 'web-app/api/clients/rest-types/discounts/discounts';
import {useFetchPricingGroupsQuery} from 'web-app/react/bookings-finance/hooks/use-fetch-pricing-groups-query';
import {useIsAutomaticDecouplingEnabled} from 'web-app/react/routes/account/institution/automatic-invoicing-config/hooks/use-is-automatic-decoupling-enabled';
import PricingGroupBehaviors from 'web-app/behaviors/pricing-group-behavior-ids';
import {mergeUnique, filter} from 'web-app/util/array';
import {useAutomaticInvoicingData} from 'web-app/react/routes/account/institution/automatic-invoicing-config/hooks/use-automatic-invoicing-data';
import {useResilientRestQuery} from '@famly/stat_apollo-query';
import * as Warnable from 'web-app/util/warnable';
import * as SentryService from 'web-app/services/sentry';

import {SessionsQuery} from '../queries/sessions.rest-query';
import {
    transformApiAutomaticInvoicingConfigToBillingFrequencies,
    transformApiBillingProfileToBillingProfile,
    transformApiDiscountToDiscount,
    transformApiProductToFee,
    transformApiSessionToTuition,
} from '../transformers/api-transformers';
import {ProductsQuery} from '../queries/products.rest-query';
import type * as Types from '../types';
import {Discounts} from '../queries/discounts.rest-query';
import {AUTOMATIC_PRICING_GROUP_ID} from '../constants';

export type SimplifiedUsBillingPlan = {
    tuitions: Types.Tuition[];
    billingFrequencies: Types.BillingFrequency[];
    fees: Types.Fee[];
    discounts: Types.Discount[];
    billingProfiles: Types.BillingProfile[];
    pricingGroups: Array<Types.InitialPricingGroup>;
    automaticInvoicing: ReturnType<typeof useAutomaticInvoicingData>;
    hasAutomaticPricingGroup: boolean;
    isAutomaticBillingEnabled: boolean;
    isAutomaticDecouplingEnabled: boolean;
    isLoading: boolean;
};

export const useSimplifiedUsBillingPlanData = ({
    siteId,
    initialTuitions,
    initialFees,
    customDiscounts,
    startDate,
}: {
    siteId: string;
    initialTuitions: Array<Types.Tuition['id']>;
    initialFees: Array<Types.Fee['id']>;
    customDiscounts: Types.DiscountCustom[];
    startDate: null | Moment;
}): Warnable.Warnable<SimplifiedUsBillingPlan, string> => {
    const {data: tuitionsData, loading: isSessionsLoading} = useResilientRestQuery<SessionsResponse, SessionsVariables>(
        SessionsQuery,
        {
            variables: {
                institutionId: siteId,
                validOn: startDate?.format('YYYY-MM-DD') ?? '',
                includeDiscontinued: false,
                behaviors: 0,
                includeHiddenByUser: true,
            },
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'cache-and-network',
        },
    );

    const {data: pricingGroupsData, loading: isLoadingPricingGroups} = useFetchPricingGroupsQuery({
        variables: {
            institutionSetId: siteId,
            institutionSetIds: [siteId],
        },
    });

    const {
        pricingGroups,
        hasAutomaticPricingGroup,
    }: {pricingGroups: Array<Types.InitialPricingGroup>; hasAutomaticPricingGroup: boolean} = useMemo(() => {
        if (!pricingGroupsData) {
            return {pricingGroups: [], hasAutomaticPricingGroup: false};
        }
        const canChooseAutoMove = pricingGroupsData.finance.pricingGroups.behaviors.some(
            behavior => behavior.behaviorId === PricingGroupBehaviors.CanChooseAutoPricingGroup,
        );

        const initialPricingGroups = mergeUnique(
            pricingGroupsData.finance.pricingGroups.list,
            pricingGroupsData.finance.oldPricingGroups.list, // This seems to be always empty but we keep it for consistency
            pricingGroup => pricingGroup.pricingGroupId,
        ).map(pricingGroup => ({
            id: pricingGroup.pricingGroupId,
            title: pricingGroup.title,
        }));

        const automaticPricingGroup: null | Types.InitialPricingGroup = canChooseAutoMove
            ? {
                  id: AUTOMATIC_PRICING_GROUP_ID,
                  title: i18next.t(
                      'react.simplifiedUsBillingPlan.translations.json.pricingGroups.pricingGroupOptions.automatic.label',
                  ),
              }
            : null;
        const pricingGroups = [automaticPricingGroup, ...initialPricingGroups].filter(hasValue);

        return {pricingGroups, hasAutomaticPricingGroup: canChooseAutoMove};
    }, [pricingGroupsData]);

    const [billingProfiles, {loading: isGetBillingProfilesLoading}] = useTransformQuery<
        GetBillingProfilesQuery,
        GetBillingProfilesQueryVariables
    >()(
        GetBillingProfiles,
        {
            variables: {
                institutionId: siteId,
            },
        },
        ({data}): Warnable.Warnable<Types.BillingProfile[], string> => {
            if (!data) {
                return Warnable.resolve([]);
            }

            return Warnable.map(
                Warnable.all(data.finance.billingProfiles.list.map(transformApiBillingProfileToBillingProfile)),
                filter(hasValue),
            );
        },
    );

    const tuitions = useMemo(() => {
        if (!tuitionsData || billingProfiles.value.length === 0) {
            return [];
        }

        const tuitionsWithWarnings = tuitionsData.result.sessions
            .filter(session => session.sessionType === 'package')
            .map(
                transformApiSessionToTuition({
                    billingProfiles: billingProfiles.value,
                    hiddenTuitionsToDisplay: initialTuitions,
                }),
            );

        const tuitions = Warnable.dispatchWarnings(Warnable.all(tuitionsWithWarnings), warnings => {
            SentryService.captureMessage(`Fetched tuitions warnings: ${warnings.map(w => w.warning).join(', ')}`);
        });

        return tuitions.filter(hasValue);
    }, [billingProfiles.value, initialTuitions, tuitionsData]);

    const [automaticInvoicingConfig, {loading: isFetchAutomaticInvoicingConfigLoading}] = useTransformQuery<
        FetchAutomaticInvoicingConfigQuery,
        FetchAutomaticInvoicingConfigQueryVariables
    >()(
        FetchAutomaticInvoicingConfig,
        {
            variables: {
                siteSetId: siteId,
            },
        },
        ({data}) => {
            if (!data) {
                return {
                    billingFrequencies: [],
                    isAutomaticBillingEnabled: false,
                };
            }

            return {
                billingFrequencies: transformApiAutomaticInvoicingConfigToBillingFrequencies(
                    data.finance.invoices.automaticInvoicing.automaticInvoicingConfig,
                ),
                isAutomaticBillingEnabled:
                    data.finance.invoices.automaticInvoicing.automaticInvoicingConfig?.sendInvoicesAutomatically,
            };
        },
    );

    const {data: productsData, loading: isProductsLoading} = useResilientRestQuery<ProductsData, ProductsVariables>(
        ProductsQuery,
        {
            variables: {
                siteId,
                includeDiscontinued: false,
                includeHiddenByUser: true,
                validOn: startDate?.format('YYYY-MM-DD') ?? '',
            },
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'cache-and-network',
        },
    );

    const fees = useMemo(() => {
        if (!productsData) {
            return [];
        }

        const fees = productsData.result.products
            .map(transformApiProductToFee({hiddenFeesToDisplay: initialFees}))
            .filter(hasValue);

        return fees;
    }, [initialFees, productsData]);

    const {data: discountsData, loading: isDiscountsLoading} = useResilientRestQuery<
        DiscountsQuery,
        DiscountsQueryVariables
    >(Discounts, {
        variables: {
            institutionId: siteId,
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-and-network',
    });

    const discounts = useMemo(() => {
        if (!discountsData) {
            return [];
        }
        return discountsData.result.discounts
            .map(discount => transformApiDiscountToDiscount(discount))
            .concat(customDiscounts);
    }, [discountsData, customDiscounts]);

    const {isAutomaticDecouplingEnabled} = useIsAutomaticDecouplingEnabled(siteId);

    const automaticInvoicing = useAutomaticInvoicingData(siteId);

    return Warnable.map(Warnable.allFields({billingProfiles}), ({billingProfiles}) => ({
        tuitions,
        billingFrequencies: automaticInvoicingConfig.billingFrequencies,
        fees,
        discounts,
        billingProfiles,
        pricingGroups,
        automaticInvoicing,
        hasAutomaticPricingGroup,
        isAutomaticBillingEnabled: automaticInvoicingConfig.isAutomaticBillingEnabled || false,
        isAutomaticDecouplingEnabled: isAutomaticDecouplingEnabled ?? false,
        isLoading:
            isSessionsLoading ||
            isGetBillingProfilesLoading ||
            isProductsLoading ||
            isFetchAutomaticInvoicingConfigLoading ||
            isDiscountsLoading ||
            isLoadingPricingGroups,
    }));
};
